<template>
    <div class="Section Features" id="features">
        <h2>Wir <span>vereinfachen</span> alle Ihre Aufgaben</h2>
        <div class="Content">
            <div class="Feature">
                <div class="Text">
                    <h3>Die Taxisoftware von morgen</h3>
                    <div class="VideoWrap">
                        <video
                            class="Video"
                            muted
                            controls
                            playsinline
                            ref="featureVideo"
                            poster="/product/tf-demo-poster.jpg"
                            preload="none"
                        >
                            <source data-src="/product/tf-demo.mp4" type="video/mp4" />
                        </video>
                        <div class="VideoOverlay" v-if="!videoLoaded" @click="loadVideo">
                            <div class="PlayButton">
                                <i class="ri-play-fill"></i>
                            </div>
                            <div class="OverlayText">Video abspielen</div>
                        </div>
                    </div>

                    <p>
                        Umfassende Managementlösung, die speziell für die Anforderungen und Abläufe
                        von Taxiunternehmen entwickelt wurde. Durch die intuitive Benutzeroberfläche
                        und leistungsfähige Analysewerkzeuge können Sie Ihren Betrieb effizienter
                        und transparenter gestalten.
                        <br /><br />
                        Die Plattform integriert alle notwendigen Funktionen, von der
                        Fahrzeugverfolgung bis hin zur Umsatzanalyse, in einem einzigen, leicht
                        zugänglichen System.
                    </p>
                </div>
            </div>

            <h2 class="Features-Title">Auf einem <span>Blick</span></h2>
            <div class="Features-Grid">
                <div class="Feature-Item">
                    <i class="ri-focus-line"></i>
                    <h3>Übersicht über Ihr Unternehmen</h3>
                    <p>
                        Erhalten Sie detaillierte Einblicke und Analysen über Ihr Unternehmen, um
                        fundierte Entscheidungen zu treffen.
                    </p>
                </div>

                <div class="Feature-Item">
                    <i class="ri-smartphone-line"></i>
                    <h3>Integrierte Fahrer-App</h3>
                    <p>
                        Ermöglichen Sie Ihren Fahrern den Zugriff auf Fahrtdaten und Optimierung der
                        Routen direkt über ihr Smartphone.
                    </p>
                </div>

                <div class="Feature-Item">
                    <i class="ri-cloud-line"></i>
                    <h3>Internetbasierte Verwaltung</h3>
                    <p>
                        Verwalten Sie Ihr Geschäft von überall aus mit einer Cloud-Plattform, die
                        Ihnen maximale Flexibilität bietet.
                    </p>
                </div>

                <div class="Feature-Item">
                    <i class="ri-shield-check-line"></i>
                    <h3>Erfüllung gesetzlicher Anforderungen</h3>
                    <p>
                        Bleiben Sie immer auf dem neuesten Stand der gesetzlichen Bestimmungen und
                        vermeiden Sie rechtliche Probleme.
                    </p>
                </div>
                <div class="Feature-Item">
                    <i class="ri-brain-line"></i>
                    <h3>Über 30 Jahre Erfahrung</h3>
                    <p>
                        Das System wurde mit direktem Input und Erfahrungen von Taxi- und
                        Mietwagenunternehmern und -fahrern entwickelt, um sicherzustellen, dass es
                        den Bedürfnissen der Kunden entspricht.
                    </p>
                </div>

                <div class="Feature-Item">
                    <i class="ri-pie-chart-line"></i>
                    <h3>Advanced Analyse</h3>
                    <p>
                        Greifen Sie auf fortgeschrittene Analysetools zu, um die Leistung Ihres
                        Unternehmens genau zu verstehen und zu verbessern.
                    </p>
                </div>
            </div>

            <div class="LearnMore-Section">
                <div class="Container">
                    <div class="LearnMore-Content">
                        <h3>Entdecken Sie alle Details</h3>
                        <p>
                            Erfahren Sie mehr über unsere umfassenden Funktionen und wie TaxiFusion
                            Ihr Taxiunternehmen noch effizienter machen kann.
                        </p>
                        <router-link to="/features">
                            <SiteButton>
                                Alle Features entdecken
                                <i class="ri-arrow-right-line" style="margin-left: 8px"></i>
                            </SiteButton>
                        </router-link>
                    </div>
                </div>
            </div>

            <div class="Feature Feature--App">
                <div class="Text">
                    <h3>Die App für den Fahrer</h3>
                    <p>
                        Die Fahrer-App ermöglicht es den Fahrern, auf wichtige Informationen
                        zuzugreifen und ihre Fahrten effizient zu verwalten. Mit
                        Echtzeit-Statistiken und Zugriff auf Betriebsdaten können Fahrer ihre
                        Leistung verbessern. Die App ist benutzerfreundlich gestaltet und bietet den
                        Fahrern eine nahtlose Integration in das Gesamtsystem.
                    </p>

                    <div class="AppStoreIcons">
                        <a
                            href="https://apps.apple.com/de/app/taxifusion-driver-app/id6523420111?l=en-GB"
                            target="_blank"
                            ref="noopener noreferrer"
                        >
                            <img
                                src="/app-store-icon.png"
                                alt="TaxiFusion Fahrer-App im Apple App Store herunterladen - iOS Version verfügbar"
                                height="50"
                                width="169"
                                style="border: 0.5px solid white; border-radius: 10px"
                                loading="lazy"
                            />
                        </a>
                        <a
                            href="https://play.google.com/store/apps/details?id=com.taxifusion.driverApp"
                            target="_blank"
                            ref="noopener noreferrer"
                        >
                            <img
                                src="/google-play-icon.png"
                                alt="TaxiFusion Fahrer-App im Google Play Store herunterladen - Android Version verfügbar"
                                height="50"
                                width="168"
                                loading="lazy"
                                style="border: 0.5px solid white; border-radius: 10px"
                            />
                        </a>
                    </div>
                </div>
                <div class="AppMockup">
                    <picture class="AppMockup-Desktop">
                        <source
                            srcset="/product/mobile-app-desktop-transparent.webp"
                            type="image/webp"
                        />
                        <source
                            srcset="/product/mobile-app-desktop-transparent.png"
                            type="image/png"
                        />
                        <img
                            src="/product/mobile-app-deskop.png"
                            alt="TaxiFusion Mobile App - Moderne und einfache Bedienung für Taxifahrer"
                            width="1200"
                            height="772"
                            loading="lazy"
                            decoding="async"
                        />
                    </picture>
                    <picture class="AppMockup-Mobile">
                        <source
                            srcset="/product/mobile-app-mobile-transparent.webp"
                            type="image/webp"
                        />
                        <source
                            srcset="/product/mobile-app-mobile-transparent.png"
                            type="image/png"
                        />
                        <img
                            src="/product/mobile-app-mobile.png"
                            alt="TaxiFusion Mobile App - MyPOS integriert für nahtlose Zahlungsabwicklung"
                            width="800"
                            height="854"
                            loading="lazy"
                            decoding="async"
                        />
                    </picture>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SiteButton from '@/components/SiteButton.vue';

export default {
    components: {
        SiteButton,
    },
    data() {
        return {
            videoLoaded: false,
            observer: null,
        };
    },
    mounted() {
        this.setupIntersectionObserver();
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        setupIntersectionObserver() {
            this.observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && !this.videoLoaded) {
                        }
                    });
                },
                {
                    threshold: 0.1,
                },
            );

            if (this.$refs.featureVideo) {
                this.observer.observe(this.$refs.featureVideo);
            }
        },
        loadVideo() {
            if (this.videoLoaded) return;

            const video = this.$refs.featureVideo;
            const source = video.querySelector('source');

            if (source && source.dataset.src) {
                source.src = source.dataset.src;
                video.load();

                video.addEventListener('loadeddata', () => {
                    video.play().catch((e) => {
                        console.log('Video autoplay failed:', e);
                    });
                });

                video.addEventListener('play', () => {
                    this.videoLoaded = true;
                });
            }
        },
    },
};
</script>

<style lang="scss">
.Features {
    background: linear-gradient(-42deg, var(--color-blue-light) 0%, var(--color-blue-dark) 100%);
    padding: 100px 40px 250px;
    margin-bottom: 100px;
    box-sizing: border-box;
    color: var(--color-text-white);
    overflow: hidden;

    @media (max-width: $breakpoint-tablet-portrait) {
        padding: 100px 20px 0;
        margin-bottom: 0px;

        overflow: hidden;
    }

    p {
        line-height: 2;
    }

    h2 {
        font-weight: 400;
        color: white;
        text-align: center;

        @media (max-width: $breakpoint-tablet-portrait) {
            text-align: left;
        }

        span {
            font-weight: 700;
        }
    }

    .Content {
        padding-top: 100px;
        max-width: 1250px;
        margin: 0 auto;

        @media (max-width: $breakpoint-tablet-portrait) {
            padding-top: 50px;
        }

        .Feature {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-start;
            gap: 60px;
            padding-bottom: 180px;
            padding-top: 180px;

            &.Feature--App {
                flex-direction: column;
                padding-bottom: 120px;
                text-align: center;

                .Text {
                    margin-top: 0;
                    margin-bottom: 40px;
                    max-width: 800px;
                    margin-left: auto;
                    margin-right: auto;
                }

                h3 {
                    font-weight: 400;
                    margin-bottom: 30px;
                    color: white;
                    width: 800px;
                    margin-left: auto;
                    margin-right: auto;

                    @media (max-width: $breakpoint-tablet-portrait) {
                        width: unset;
                    }
                }

                p {
                    color: var(--color-text-white);
                    width: 800px;
                    margin-left: auto;
                    margin-right: auto;
                    margin-bottom: 40px;

                    @media (max-width: $breakpoint-tablet-portrait) {
                        width: unset;
                    }
                }

                @media (max-width: $breakpoint-tablet-landscape) {
                    padding-bottom: 80px;

                    .Text {
                        margin-bottom: 30px;
                    }
                }
            }

            .AppMockup {
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;

                .AppMockup-Desktop {
                    display: block;
                    margin-top: 0;

                    @media (max-width: $breakpoint-tablet-landscape) {
                        display: none;
                    }

                    img {
                        max-width: 800px;
                        height: auto;
                        margin-bottom: 0;
                        margin-top: 0;
                    }
                }

                .AppMockup-Mobile {
                    display: none;

                    @media (max-width: $breakpoint-tablet-landscape) {
                        display: block;
                    }

                    img {
                        max-width: 100%;
                        height: auto;
                        margin-bottom: 0;
                    }
                }
            }

            @media (max-width: $breakpoint-tablet-portrait) {
                padding-bottom: 80px;
                padding-top: 80px;
            }

            &:first-child {
                padding-top: 0;
            }

            &:nth-child(odd) {
                .Text {
                    display: flex;
                    width: 100%;
                    flex-direction: column;
                }

                @media (max-width: $breakpoint-tablet-portrait) {
                    text-align: left;
                    flex-direction: column;
                    .Text {
                        padding-left: 0px;
                        align-items: flex-start;
                    }
                }
            }

            &:nth-child(even) {
                @media (max-width: $breakpoint-tablet-portrait) {
                    text-align: left;
                    flex-direction: column;
                    .Text {
                        padding-right: 0px;
                        align-items: flex-start;
                    }
                }
            }

            &:last-child {
                padding-bottom: 0;
                z-index: 2;
                position: relative;
                margin-bottom: -260px;

                @media (max-width: $breakpoint-tablet-landscape) {
                    margin-bottom: 0;
                }

                .Text {
                    @media (max-width: $breakpoint-tablet-portrait) {
                        padding-left: 0;
                    }
                }

                img {
                    box-shadow: none;
                }
            }

            h3 {
                font-weight: 400;
                margin-bottom: 30px;
                color: white;
            }

            .AppStoreIcons {
                display: flex;
                gap: 20px;
                margin-top: 30px;
                justify-content: center;
                align-items: center;

                @media (max-width: $breakpoint-tablet-portrait) {
                    flex-direction: column;
                    a > img {
                        width: 70%;
                        height: auto;
                        max-width: 170px;
                        margin-bottom: 0px;
                    }
                }

                a {
                    display: inline-block;
                }
            }
            img {
                max-width: 100%;
                display: block;
                box-shadow: 0 20px 30px 0 rgba(0, 0, 0, 0.15);
                border-radius: 10px;
                margin-bottom: 30px;

                @media (max-width: $breakpoint-tablet-portrait) {
                    width: 800px;
                    max-width: unset;
                    margin-top: 30px;
                    margin-bottom: 80px;
                }
            }

            p {
                color: var(--color-text-white);
            }
        }

        h3,
        p {
            width: 800px;
            margin: 0 auto;

            @media (max-width: $breakpoint-tablet-portrait) {
                width: 80vw;
                margin: 0;
            }
        }
    }

    .LearnMore-Section {
        padding: 80px 0;
        background: rgba(255, 255, 255, 0.02);
        border-top: 0.5px solid rgba(255, 255, 255, 0.06);
        border-bottom: 0.5px solid rgba(255, 255, 255, 0.06);
        margin-bottom: 100px;

        @media (max-width: $breakpoint-tablet-portrait) {
            padding: 60px 0;
            margin-bottom: 60px;
        }

        .LearnMore-Content {
            text-align: center;
            max-width: 800px;
            margin: 0 auto;

            @media (max-width: $breakpoint-mobile) {
                text-align: left;
            }

            h3 {
                font-size: 2.2rem;
                font-weight: 700;
                color: white;
                margin-bottom: 1rem;
                line-height: 1.2;

                @media (max-width: $breakpoint-tablet-portrait) {
                    font-size: 1.8rem;
                }

                @media (max-width: $breakpoint-mobile) {
                    font-size: 1.6rem;
                }
            }

            p {
                font-size: 1.1rem;
                color: rgba(255, 255, 255, 0.8);
                line-height: 1.6;
                margin-bottom: 2rem;
                max-width: 500px;
                margin-left: auto;
                margin-right: auto;

                @media (max-width: $breakpoint-tablet-portrait) {
                    font-size: 1rem;
                }
            }
        }
    }

    .VideoWrap {
        position: relative;
        padding-bottom: 56.25%; /* 16:9 */
        height: 0;
        margin-bottom: 30px;
        width: 100%;

        @media (max-width: $breakpoint-tablet-portrait) {
            width: 80vw;
            margin: 0;
        }

        .Video {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 10px;
        }

        .VideoOverlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.3);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            border-radius: 10px;
            transition: background-color 0.3s ease;

            &:hover {
                background: rgba(0, 0, 0, 0.2);
            }

            .PlayButton {
                width: 80px;
                height: 80px;
                background: rgba(255, 255, 255, 0.9);
                border-radius: 50%;
                display: flex;
                justify-content: center;
                align-items: center;
                margin-bottom: 20px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                transition: all 0.3s ease;

                &:hover {
                    background: white;
                    transform: scale(1.1);
                }

                i {
                    color: var(--color-blue-dark);
                    font-size: 32px;
                    margin-left: 4px;
                }
            }

            .OverlayText {
                color: white;
                font-size: 16px;
                font-weight: 500;
                text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
            }
        }
    }

    .Features-Title {
        margin-top: 4rem;
        margin-bottom: 2rem;
    }

    .Features-Grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;
        max-width: 1000px;
        margin: 0 auto;

        @media (max-width: $breakpoint-tablet-landscape) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (max-width: $breakpoint-tablet-portrait) {
            grid-template-columns: repeat(1, 1fr);
        }
    }

    .Feature-Item {
        border-radius: 8px;
        padding: 2rem;
        display: flex;
        flex-direction: column;
        text-align: left;

        @media (max-width: $breakpoint-tablet-portrait) {
            padding: 2rem 0;
        }

        h3 {
            font-size: 1.25rem;
            font-weight: 700;
            margin: 0;
            margin-bottom: 1rem;
            color: #ffffff;
            width: auto;
        }

        p {
            margin: 0;
            width: auto;
            font-size: 1rem;
            color: #ffffff;
        }

        i {
            font-size: 2rem; /* Larger icons */
            color: #ffffff; /* Theme color for icons */
            margin-bottom: 1rem;
        }
    }
}
</style>
